import { Link, useLocation } from "react-router-dom";
import { useEffect, useId, useState } from "react";
import { useAuth } from "@/auth/AuthContext.jsx";
import { CONTACT_ROUTE, CART_ROUTE, CUSTOMER_PROFILE_ROUTE, MAIN_ROUTE, NOUVEAUTES_ROUTE, ARRIVES_ROUTE, ROLES, STORE_NAME } from "@/utils/constants.js";
import LoginModal from "@/components/LoginModal.jsx";
import UserProfileMenu from "@/components/UserProfileMenu.jsx";
import GuestAccountMenu from "@/components/GuestAccountMenu.jsx";
import AdminBanner from "@/components/AdminBanner.jsx";
import { GuestAuthBanner, useGuestAuthPrompt } from "@/components/GuestAuthPrompt.jsx";
import { HeaderStoreContacts, hasHeaderStoreContact } from "@/components/StoreContacts.jsx";
import BrandMark from "@/components/BrandMark.jsx";
import { useCart } from "@/cart/CartContext.jsx";
import { useSiteTopHeight } from "@/hooks/useSiteTopHeight.js";
import { useStoreInfo } from "@/store/StoreInfoContext.jsx";
import { usePlatformWalkthrough } from "@/components/usePlatformWalkthrough.jsx";
import { useProductHighlightPopup } from "@/components/useProductHighlightPopup.jsx";
import { useProductHighlightAvailability } from "@/hooks/useProductHighlightAvailability.js";
import MenuItemLabel from "@/components/MenuItemLabel.jsx";
import { HEADER_NAV_ITEMS } from "@/utils/profileMenus.js";
import { isStaffRole } from "@/utils/staffAccess.js";
import {
    WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT,
    WALKTHROUGH_MOBILE_NAV_READY_EVENT,
    WALKTHROUGH_OPEN_MOBILE_NAV_EVENT
} from "@/components/Walkthrough.jsx";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock.js";
import "./header.css";

const MOBILE_HEADER_MQ = "(max-width: 768px)";

export default function Header() {
    const { isAuthenticated, role, loginRequired, clearLoginRequired } = useAuth();
    const location = useLocation();
    const { storeInfo } = useStoreInfo();
    const [showLogin, setShowLogin] = useState(false);
    const [loginMode, setLoginMode] = useState("login");
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const mobileNavId = useId();
    const isAdmin = isAuthenticated && isStaffRole(role);
    const isCustomer = isAuthenticated && role === ROLES.CUSTOMER;
    const { itemCount } = useCart();
    const { showBanner: showGuestBanner } = useGuestAuthPrompt();
    const { walkthroughActive, WalkthroughHost } = usePlatformWalkthrough({ loginOpen: showLogin });
    const { ProductHighlightHost } = useProductHighlightPopup({
        walkthroughActive,
        loginOpen: showLogin
    });
    const { hasNew, hasRestocked } = useProductHighlightAvailability();

    useSiteTopHeight();

    useEffect(() => {
        const onOpenMobileNav = () => setMobileMenuOpen(true);
        const onCloseMobileNav = () => setMobileMenuOpen(false);
        window.addEventListener(WALKTHROUGH_OPEN_MOBILE_NAV_EVENT, onOpenMobileNav);
        window.addEventListener(WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT, onCloseMobileNav);
        return () => {
            window.removeEventListener(WALKTHROUGH_OPEN_MOBILE_NAV_EVENT, onOpenMobileNav);
            window.removeEventListener(WALKTHROUGH_CLOSE_MOBILE_NAV_EVENT, onCloseMobileNav);
        };
    }, []);

    useEffect(() => {
        if (!mobileMenuOpen) {
            return undefined;
        }
        let cancelled = false;
        let outerFrameId = 0;
        let innerFrameId = 0;
        outerFrameId = window.requestAnimationFrame(() => {
            innerFrameId = window.requestAnimationFrame(() => {
                if (!cancelled) {
                    window.dispatchEvent(new Event(WALKTHROUGH_MOBILE_NAV_READY_EVENT));
                }
            });
        });
        return () => {
            cancelled = true;
            window.cancelAnimationFrame(outerFrameId);
            window.cancelAnimationFrame(innerFrameId);
        };
    }, [mobileMenuOpen]);

    useEffect(() => {
        if (loginRequired) {
            setShowLogin(true);
            clearLoginRequired();
        }
    }, [loginRequired, clearLoginRequired]);

    useEffect(() => {
        document.body.classList.toggle("admin-session", isAdmin);
        return () => document.body.classList.remove("admin-session");
    }, [isAdmin]);

    useEffect(() => {
        const media = window.matchMedia(MOBILE_HEADER_MQ);
        const sync = () => {
            if (!media.matches) {
                setMobileMenuOpen(false);
            }
        };
        sync();
        media.addEventListener("change", sync);
        return () => media.removeEventListener("change", sync);
    }, []);

    useEffect(() => {
        if (!mobileMenuOpen) {
            return undefined;
        }
        lockBodyScroll();
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => {
            unlockBodyScroll();
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [mobileMenuOpen]);

    const openLogin = () => {
        setLoginMode("login");
        setShowLogin(true);
    };

    const openRegister = () => {
        setLoginMode("register");
        setShowLogin(true);
    };

    const closeMobileMenu = () => setMobileMenuOpen(false);

    const tourAttr = (name) => ({ "data-tour": name });

    const renderNavLinks = (forMobile) => (
        <>
            <Link
                to={MAIN_ROUTE}
                className="header-nav-link"
                {...tourAttr("nav-catalogue")}
                onClick={forMobile ? closeMobileMenu : undefined}
            >
                <MenuItemLabel icon={HEADER_NAV_ITEMS.catalogue.icon}>{HEADER_NAV_ITEMS.catalogue.label}</MenuItemLabel>
            </Link>
            {hasNew && (
                <Link
                    to={NOUVEAUTES_ROUTE}
                    className="header-nav-link"
                    {...tourAttr("nav-nouveautes")}
                    onClick={forMobile ? closeMobileMenu : undefined}
                >
                    <MenuItemLabel icon={HEADER_NAV_ITEMS.nouveautes.icon}>{HEADER_NAV_ITEMS.nouveautes.label}</MenuItemLabel>
                </Link>
            )}
            {hasRestocked && (
                <Link
                    to={ARRIVES_ROUTE}
                    className="header-nav-link"
                    {...tourAttr("nav-arrives")}
                    onClick={forMobile ? closeMobileMenu : undefined}
                >
                    <MenuItemLabel icon={HEADER_NAV_ITEMS.arrives.icon}>{HEADER_NAV_ITEMS.arrives.label}</MenuItemLabel>
                </Link>
            )}
            <Link
                to={CONTACT_ROUTE}
                className="header-nav-link"
                {...tourAttr("nav-contact")}
                onClick={forMobile ? closeMobileMenu : undefined}
            >
                <MenuItemLabel icon={HEADER_NAV_ITEMS.contact.icon}>{HEADER_NAV_ITEMS.contact.label}</MenuItemLabel>
            </Link>
            {/* Cart / Mes achats live in the top-right Compte menu — omit them from the mobile drawer. */}
            {!isAdmin && !forMobile && (
                <>
                    <Link
                        to={CART_ROUTE}
                        className="header-cart-link header-nav-link"
                        {...tourAttr("nav-cart")}
                    >
                        <MenuItemLabel
                            icon={HEADER_NAV_ITEMS.cart.icon}
                            badge={isCustomer && itemCount > 0 ? itemCount : null}
                        >
                            {HEADER_NAV_ITEMS.cart.label}
                        </MenuItemLabel>
                    </Link>
                    <Link
                        to={`${CUSTOMER_PROFILE_ROUTE}?section=purchases`}
                        className="header-nav-link"
                        {...tourAttr("nav-purchases")}
                    >
                        <MenuItemLabel icon={HEADER_NAV_ITEMS.purchases.icon}>{HEADER_NAV_ITEMS.purchases.label}</MenuItemLabel>
                    </Link>
                </>
            )}
        </>
    );

    return (
        <>
            <div className="site-top-fixed">
                {isAdmin && <AdminBanner />}
                <header className="site-header">
                    <div className="container header-inner">
                        <button
                            type="button"
                            className={`header-menu-toggle${mobileMenuOpen ? " is-open" : ""}`}
                            aria-expanded={mobileMenuOpen}
                            aria-controls={mobileNavId}
                            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                            onClick={() => setMobileMenuOpen((open) => !open)}
                        >
                            <span className="header-menu-toggle-bars" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                            </span>
                        </button>
                        <div className="header-left">
                            <BrandMark name={STORE_NAME} size="header" />
                            {hasHeaderStoreContact(storeInfo) && (
                                <HeaderStoreContacts storeInfo={storeInfo} />
                            )}
                        </div>
                        <nav className="header-nav" aria-label="Navigation principale">
                            {renderNavLinks(false)}
                        </nav>
                        <div className="header-account" data-tour="account-menu">
                            {isAuthenticated ? (
                                <UserProfileMenu />
                            ) : (
                                <GuestAccountMenu
                                    onLogin={openLogin}
                                    onRegister={openRegister}
                                />
                            )}
                        </div>
                    </div>
                </header>
                <div
                    className={`header-mobile-nav-backdrop${mobileMenuOpen ? " is-open" : ""}`}
                    aria-hidden="true"
                    onClick={closeMobileMenu}
                />
                <nav
                    id={mobileNavId}
                    className={`header-mobile-nav${mobileMenuOpen ? " is-open" : ""}`}
                    aria-label="Menu de navigation"
                    aria-hidden={!mobileMenuOpen}
                >
                    <div className="header-mobile-nav-header">
                        <button
                            type="button"
                            className="header-menu-toggle header-mobile-nav-close is-open"
                            aria-label="Fermer le menu"
                            onClick={closeMobileMenu}
                        >
                            <span className="header-menu-toggle-bars" aria-hidden="true">
                                <span />
                                <span />
                                <span />
                            </span>
                        </button>
                        <p className="header-mobile-nav-title">{STORE_NAME}</p>
                    </div>
                    {renderNavLinks(true)}
                </nav>
                {showGuestBanner && !showLogin && (
                    <GuestAuthBanner onLogin={openLogin} onRegister={openRegister} />
                )}
            </div>
            <div className="site-top-spacer" aria-hidden="true" />
            {showLogin && (
                <LoginModal
                    initialMode={loginMode}
                    returnTo={`${location.pathname}${location.search}`}
                    onClose={() => setShowLogin(false)}
                />
            )}
            <WalkthroughHost />
            <ProductHighlightHost />
        </>
    );
}
